// Email (4 October 2026): email broadcasts, a flow's email step, email threads in the Inbox, and email when
// WhatsApp cannot deliver a service message, through whichever provider the platform chose.
//
// `services/email`. Hand-typed until the generated client is next fetched from a running stack. The request
// path is the house core, so the envelope, token refresh and ApiError mapping are the same as everywhere.
//
// A console asks `getStatus()` before it offers anything email: `available: false` means the platform has no
// provider set up, and every email option stays hidden.

import { http } from './_core/request.js';
import type { Envelope } from './_core/envelope.js';

export interface EmailStatus {
  /** Whether email can be sent on the platform at all. */
  available: boolean;
  /** The business's address on the platform's domain, e.g. `northgate@post.repletoryreach.co.za`. Customers write to it. */
  address?: string | null;
  /** The address email goes from now: the own-domain address once that domain is verified, else `address`. */
  sending_from?: string | null;
  /** Where replies go. */
  reply_to?: string | null;
  /** Whether this organisation's WhatsApp service messages fall back to email. */
  fallback_enabled: boolean;
}

export interface EmailSettings {
  /** The name in the From line. Blank means the business's name. */
  sender_name?: string | null;
  /** Where replies go. Blank means the platform address, so replies reach the Inbox. */
  reply_to?: string | null;
  /** WhatsApp utility and authentication messages that cannot be delivered go by email instead. Off by default. */
  fallback_enabled: boolean;
  /** The own domain to send from once it is verified, and the part before the @ on it. */
  own_domain_id?: string | null;
  own_local_part?: string | null;
  platform_address?: string | null;
  sending_from?: string | null;
  effective_reply_to?: string | null;
  updated_at?: string | null;
}

export interface UpdateEmailSettings {
  sender_name?: string;
  reply_to?: string;
  fallback_enabled?: boolean;
  /** `false` goes back to sending from the platform address. */
  use_own_domain?: boolean;
  own_domain_id?: string;
  own_local_part?: string;
}

/** One record to add at the domain's DNS host. */
export interface EmailDnsRecord {
  type: string;
  host: string;
  value: string;
  /** Whether the provider found it, when it says. */
  verified?: boolean | null;
}

export interface EmailDomain {
  id: string;
  domain: string;
  status: 'pending' | 'verified' | 'failed';
  /** The records were made with a provider that no longer sends: check again (the records may change). */
  check_again: boolean;
  records: EmailDnsRecord[];
  detail?: string | null;
  verified_at?: string | null;
  checked_at?: string | null;
  created_at: string;
}

/** One part of a message in the broadcast format. */
export interface EmailBlock {
  kind: 'paragraph' | 'button';
  text: string;
  /** A button's address (https). */
  url?: string;
}

export interface EmailPreview {
  from: string;
  reply_to: string;
  subject: string;
  text: string;
  html: string;
  size_bytes: number;
  max_bytes: number;
}

/** One email's outcome, as the platform console's test send reports it. */
export interface EmailSendResult {
  message_id?: string | null;
  status: 'queued' | 'sent' | 'delivered' | 'bounced' | 'complained' | 'failed' | 'refused';
  error_code?: string | null;
  error?: string | null;
  charged_minor: number;
  duplicate: boolean;
}

export interface EmailAdminStatus {
  available: boolean;
  active_provider?: string | null;
  backup_provider?: string | null;
  inbound_provider?: string | null;
  sending_domain: string;
  /** Why email is not available, in words. */
  reason?: string | null;
  last_24_hours: { status: string; count: number }[];
  last_webhook?: { provider_code: string; received_at: string; outcome?: string | null } | null;
  suppressions: number;
  platform_wide_suppressions: number;
}

export function getStatus(): Promise<Envelope<EmailStatus>> {
  return http.get<Envelope<EmailStatus>>('/v1/email/status');
}

export function getSettings(): Promise<Envelope<EmailSettings>> {
  return http.get<Envelope<EmailSettings>>('/v1/email/settings');
}

/** Owner and admins only (an agent is refused 403). */
export function updateSettings(req: UpdateEmailSettings): Promise<Envelope<EmailSettings>> {
  return http.put<Envelope<EmailSettings>>('/v1/email/settings', { body: req });
}

export function listDomains(): Promise<Envelope<EmailDomain[]>> {
  return http.get<Envelope<EmailDomain[]>>('/v1/email/domains');
}

/** Owner and admins only. Returns the DNS records to add; the domain sends once they verify. */
export function addDomain(req: { domain: string }): Promise<Envelope<EmailDomain>> {
  return http.post<Envelope<EmailDomain>>('/v1/email/domains', { body: req });
}

/** Owner and admins only. Asks the provider whether the records are found yet. */
export function checkDomain(id: string): Promise<Envelope<EmailDomain>> {
  return http.post<Envelope<EmailDomain>>(`/v1/email/domains/${encodeURIComponent(id)}/check`);
}

/** Owner and admins only. Email goes from the platform address again. */
export function removeDomain(id: string): Promise<void> {
  return http.delete<void>(`/v1/email/domains/${encodeURIComponent(id)}`);
}

/** What a message becomes as an email. `marketing` adds the unsubscribe link and headers. */
export function preview(req: { subject?: string; text?: string; blocks?: EmailBlock[]; marketing?: boolean }): Promise<Envelope<EmailPreview>> {
  return http.post<Envelope<EmailPreview>>('/v1/email/preview', { body: req });
}

/** Platform console: routing, the last day's sends by status, the last webhook and the suppression list. */
export function adminStatus(): Promise<Envelope<EmailAdminStatus>> {
  return http.get<Envelope<EmailAdminStatus>>('/v1/admin/email/status');
}

/** Platform console: a test email through the chosen provider. Charged to nobody. */
export function adminTest(req: { to: string; subject?: string; text?: string }): Promise<Envelope<EmailSendResult>> {
  return http.post<Envelope<EmailSendResult>>('/v1/admin/email/test', { body: req });
}
